import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PencilIcon, TrashIcon, PlusIcon, UsersIcon, EyeIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import api from '../../services/api';
import { PermissionGate, useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import TableColumnFilter from '../../components/TableColumnFilter';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';

interface Customer {
  id: string;
  accountName: string;
  accountShortName: string;
  effectiveDate: string;
  endDate: string | null;
  contactNumber: string;
  emailAddress: string;
  sbu: string;
  accountOwner: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  salesRepresentative: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const sbuOptions = [
  { value: 'BU1', label: 'BU1' },
  { value: 'BU2', label: 'BU2' },
  { value: 'BU3', label: 'BU3' },
  { value: 'BU4', label: 'BU4' },
  { value: 'BU5', label: 'BU5' },
  { value: 'BU6', label: 'BU6' },
  { value: 'BU7', label: 'BU7' },
  { value: 'BU8', label: 'BU8' },
  { value: 'BU9', label: 'BU9' },
  { value: 'BU10', label: 'BU10' },
];

const CustomerList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const appliedSearch = searchParams.get('search') ?? '';
  const filterName = searchParams.get('name') ?? '';
  const filterShortName = searchParams.get('shortName') ?? '';
  const filterSbu = searchParams.get('sbu') ?? '';
  const filterEmail = searchParams.get('email') ?? '';
  const filterAccountOwner = searchParams.get('accountOwner') ?? '';
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [offset, setOffset] = useState(0);
  const { can, loading: permissionsLoading } = useDynamicPermissions();

  const canView = can('customers', 'read');
  const canCreate = can('customers', 'create');

  useEffect(() => {
    setSearchInput(appliedSearch);
  }, [appliedSearch]);

  const { data: usersDropdownData } = useQuery({
    queryKey: ['users-dropdown'],
    queryFn: async () => {
      const response = await api.get('/users/dropdown');
      return response.data;
    },
  });

  const accountOwnerOptions = useMemo(
    () =>
      usersDropdownData?.users?.map((u: { id: string; firstName?: string; lastName?: string; email?: string }) => ({
        value: u.id,
        label: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || 'Unknown',
      })) ?? [],
    [usersDropdownData]
  );

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: offset.toString(),
      });
      if (appliedSearch) params.append('search', appliedSearch);
      if (filterName) params.append('accountName', filterName);
      if (filterShortName) params.append('accountShortName', filterShortName);
      if (filterSbu) params.append('sbu', filterSbu);
      if (filterEmail) params.append('emailAddress', filterEmail);
      if (filterAccountOwner) params.append('accountOwnerId', filterAccountOwner);

      const response = await api.get(`/customers?${params}`);
      setCustomers(response.data.customers);
      setTotal(response.data.total);
      setError(null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch customers';
      const errorDetails = err.response?.data?.details || '';
      setError(errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage);
    } finally {
      setLoading(false);
    }
  }, [pageSize, offset, appliedSearch, filterName, filterShortName, filterSbu, filterEmail, filterAccountOwner]);

  useEffect(() => {
    if (permissionsLoading) return;
    if (canView) fetchCustomers();
  }, [canView, fetchCustomers, permissionsLoading]);

  const handleDelete = async (id: string, accountName: string) => {
    confirmAlert({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete customer "${accountName}"? This action cannot be undone.`,
      buttons: [
        {
          label: 'Cancel',
          onClick: () => {},
          className: 'px-6 py-2.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm',
        },
        {
          label: 'Delete',
          onClick: async () => {
            try {
              await api.delete(`/customers/${id}`);
              toast.success('Customer deleted successfully');
              fetchCustomers();
            } catch (err: any) {
              const errorMessage = err.response?.data?.error || 'Failed to delete customer';
              toast.error(errorMessage);
            }
          },
          className: 'px-6 py-2.5 text-sm text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 shadow-sm',
        },
      ],
    });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    patchSearchParams({ search: searchInput.trim() || null });
    setOffset(0);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    if (value === '') {
      patchSearchParams({ search: null });
      setOffset(0);
    }
  };

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'accountName':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterName}
              placeholder="Filter by name..."
              onChange={(v) => {
                patchSearchParams({ name: typeof v === 'string' && v ? v : null });
                setOffset(0);
              }}
            />
          );
        case 'accountShortName':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterShortName}
              placeholder="Filter by short name..."
              onChange={(v) => {
                patchSearchParams({ shortName: typeof v === 'string' && v ? v : null });
                setOffset(0);
              }}
            />
          );
        case 'sbu':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={sbuOptions}
              value={filterSbu}
              placeholder="All SBUs"
              onChange={(v) => {
                patchSearchParams({ sbu: typeof v === 'string' && v ? v : null });
                setOffset(0);
              }}
            />
          );
        case 'emailAddress':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterEmail}
              placeholder="Filter by email..."
              onChange={(v) => {
                patchSearchParams({ email: typeof v === 'string' && v ? v : null });
                setOffset(0);
              }}
            />
          );
        case 'accountOwner':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={accountOwnerOptions}
              value={filterAccountOwner}
              placeholder="All Owners"
              onChange={(v) => {
                patchSearchParams({ accountOwner: typeof v === 'string' && v ? v : null });
                setOffset(0);
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [
      filterName,
      filterShortName,
      filterSbu,
      filterEmail,
      filterAccountOwner,
      accountOwnerOptions,
      patchSearchParams,
    ]
  );

  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (appliedSearch) {
      chips.push({ id: 'search', label: 'Search', value: `"${appliedSearch}"`, color: 'blue' });
    }
    if (filterName) {
      chips.push({ id: 'name', label: 'Account Name', value: filterName, color: 'blue' });
    }
    if (filterShortName) {
      chips.push({ id: 'shortName', label: 'Short Name', value: filterShortName, color: 'blue' });
    }
    if (filterSbu) {
      chips.push({ id: 'sbu', label: 'SBU', value: filterSbu, color: 'teal' });
    }
    if (filterEmail) {
      chips.push({ id: 'email', label: 'Email', value: filterEmail, color: 'blue' });
    }
    if (filterAccountOwner) {
      const owner = accountOwnerOptions.find((o: { value: string; label: string }) => o.value === filterAccountOwner);
      chips.push({ id: 'accountOwner', label: 'Account Owner', value: owner?.label || 'Unknown', color: 'purple' });
    }
    return chips;
  }, [appliedSearch, filterName, filterShortName, filterSbu, filterEmail, filterAccountOwner, accountOwnerOptions]);

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null });
        break;
      case 'name':
        patchSearchParams({ name: null });
        break;
      case 'shortName':
        patchSearchParams({ shortName: null });
        break;
      case 'sbu':
        patchSearchParams({ sbu: null });
        break;
      case 'email':
        patchSearchParams({ email: null });
        break;
      case 'accountOwner':
        patchSearchParams({ accountOwner: null });
        break;
    }
    setOffset(0);
  };

  const handleClearAllFilters = () => {
    setSearchInput('');
    patchSearchParams({
      search: null,
      name: null,
      shortName: null,
      sbu: null,
      email: null,
      accountOwner: null,
    });
    setOffset(0);
  };

  const nextPage = () => {
    if (offset + pageSize < total) setOffset(offset + pageSize);
  };

  const prevPage = () => {
    if (offset > 0) setOffset(Math.max(0, offset - pageSize));
  };

  const getSBUDisplay = (sbu: string) => sbu;

  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            <h1 className="text-lg font-semibold text-gray-900 whitespace-nowrap">Customers</h1>
            <div className="flex items-center gap-2">
              <form onSubmit={handleSearch} className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  className="px-3 py-1.5 w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={searchInput}
                  onChange={handleSearchInputChange}
                />
                <button
                  type="submit"
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Search
                </button>
              </form>
              <PermissionGate resource="customers" action="create">
                <Link
                  to="/customers/new"
                  className="inline-flex items-center px-3 py-1.5 text-sm border border-transparent rounded-md font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                  Add Customer
                </Link>
              </PermissionGate>
            </div>
          </div>
        </div>
      </div>

      {activeFilterChips.length > 0 && (
        <div className="bg-white shadow rounded-lg mb-2 px-4 py-2 flex-shrink-0">
          <ActiveFilterChips
            chips={activeFilterChips}
            onRemove={handleRemoveFilterChip}
            onClearAll={handleClearAllFilters}
          />
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-4">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <div className="overflow-scroll flex-1" style={{ scrollbarGutter: 'stable' }}>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      {renderColumnHeader('accountName', 'Account Name')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      {renderColumnHeader('accountShortName', 'Short Name')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      {renderColumnHeader('sbu', 'SBU')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      {renderColumnHeader('emailAddress', 'Email Address')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      {renderColumnHeader('accountOwner', 'Account Owner')}
                    </th>
                    <th className="relative px-6 py-3 bg-gray-50">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {customers.map((customer) => (
                    <tr key={customer.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          <Link to={`/customers/${customer.id}`} className="text-blue-600 hover:text-blue-900">
                            {customer.accountName}
                          </Link>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {customer.accountShortName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {getSBUDisplay(customer.sbu)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <a href={`mailto:${customer.emailAddress}`} className="text-blue-600 hover:text-blue-900">
                          {customer.emailAddress}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">
                            {customer.accountOwner.firstName} {customer.accountOwner.lastName}
                          </div>
                          <div className="text-gray-500">{customer.accountOwner.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2">
                          <Link to={`/customers/${customer.id}`} className="text-blue-600 hover:text-blue-900" title="View Customer Details">
                            <EyeIcon className="h-4 w-4" />
                          </Link>
                          <PermissionGate resource="customers" action="update">
                            <Link to={`/customers/${customer.id}/edit`} className="text-yellow-600 hover:text-yellow-900" title="Edit Customer">
                              <PencilIcon className="h-4 w-4" />
                            </Link>
                          </PermissionGate>
                          <PermissionGate resource="customers" action="delete">
                            <button
                              onClick={() => handleDelete(customer.id, customer.accountName)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Customer"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-white px-4 py-2 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
              {total > 0 ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">Show</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setOffset(0);
                      }}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-700">entries</span>
                  </div>
                  <div className="text-sm text-gray-700">
                    Showing {Math.min(offset + 1, total)} to {Math.min(offset + pageSize, total)} of {total} entries
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setOffset(0)} disabled={offset === 0} className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" title="First page">&laquo;</button>
                    <button onClick={prevPage} disabled={offset === 0} className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Previous</button>
                    {(() => {
                      const totalPages = Math.ceil(total / pageSize);
                      const currentPage = Math.floor(offset / pageSize) + 1;
                      const pages: (number | string)[] = [];
                      if (totalPages <= 7) {
                        for (let i = 1; i <= totalPages; i++) pages.push(i);
                      } else if (currentPage <= 3) {
                        pages.push(1, 2, 3, 4, '...', totalPages);
                      } else if (currentPage >= totalPages - 2) {
                        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                      } else {
                        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
                      }
                      return pages.map((p, idx) =>
                        p === '...' ? (
                          <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-500">...</span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => setOffset((p as number - 1) * pageSize)}
                            className={`px-3 py-1 text-sm border rounded-md ${currentPage === p ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 hover:bg-gray-50'}`}
                          >
                            {p}
                          </button>
                        )
                      );
                    })()}
                    <button onClick={nextPage} disabled={offset + pageSize >= total} className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Next</button>
                    <button onClick={() => setOffset(Math.floor((total - 1) / pageSize) * pageSize)} disabled={offset + pageSize >= total} className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" title="Last page">&raquo;</button>
                  </div>
                </>
              ) : (
                <div className="text-sm text-gray-500 w-full text-center">No records</div>
              )}
            </div>

            {customers.length === 0 && !loading && (
              <div className="text-center py-12">
                <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No customers</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {canCreate ? 'Get started by creating a new customer account.' : 'No customer accounts available to display.'}
                </p>
                <div className="mt-6">
                  <PermissionGate resource="customers" action="create">
                    <Link to="/customers/new" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                      Add Customer
                    </Link>
                  </PermissionGate>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerList;
